import { DocumentIcon, Signer } from "assets/icons";
import Chip from "components/chip/chip";
import useModal from "components/modal/useModal";
import { dateWordShort } from "helper/helper";
import UseContext from "hooks/useContext";
import ApiDatabase from "server";
import Tooltip from "../../tooltips/Tooltips";
import {useState, useEffect} from "react";
import TableCell from "./TableCell";
import PerimeterName from "components/perimeterFilter/PerimeterName";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useScrollContext } from "./ScrollableTableWrapper";
import { toast } from "react-toastify";

const Tbodycontract = ({ champsBody, filterStatus, showPerimeter = false }) => {
  const { toggle } = useModal();
  const { companyData, setSignatureLink, setDataYousign, setIdMission, refreshLoadCompany, language } = UseContext();
  const rUser = localStorage.getItem("rUser");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState(null);
  const { hasMoved } = useScrollContext();

  useEffect(() => {
    const idFromUrl = searchParams.get('selectedId');
    if (idFromUrl) {
      setSelectedId(idFromUrl);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('selectedId');
      const newUrl = newParams.toString() ? `?${newParams.toString()}` : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [searchParams]);

  const colorContract = {
    0: "ALERT",
    1: "INFO",
  };
  const contentContract = {
    0: "À signer",
    1: "Signé",
  };

  const getAvenantNumber = (c) => c.avenant_number ?? c.avenant ?? 0;

  const openSignatureModal = (contract, missionId) => {
    toggle("doc_c_signature");

    ApiDatabase.postSignature(
      {
        id_mission: missionId,
        contract,
      },
      (data) => {
        setDataYousign({
          yousign_id: data.pYousignId,
          contract_id: contract._id,
          mission_id: missionId,
        });
        setSignatureLink(data.pSignatureYousign);
      },
      (err) => {
        console.log(err);
        toggle("doc_c_signature");
        const errorCode = err?.response?.data?.errorCode;
        if (errorCode === "CONTRACT_ALREADY_SIGNED") {
          // The api finalised the contract itself: reload so the list shows it as signed
          refreshLoadCompany();
        } else if (errorCode === "CONTRACT_DOCUMENT_UNAVAILABLE") {
          toast.error(
            language.getValue(
              "contract.documentUnavailable",
              "Le document n'est pas disponible pour le moment. Veuillez réessayer plus tard."
            )
          );
        }
      }
    );
  };

  const handleSign = (e, contract, item) => {
    e.preventDefault();
    e.stopPropagation();
    openSignatureModal(contract, item);
  };

  const handleRowClick = (item, contract) => {
    if (hasMoved) return;

    setSelectedId(contract._id);

    if (contract && contract.status === "0" && rUser !== "200") {
      openSignatureModal(contract, item?.id_mission);
    } else {
      const avenantNum = getAvenantNumber(contract);
      const suffix = avenantNum > 0 ? `_avenant_${avenantNum}` : "";
      const fileName = contract?.status === "1"
        ? `contract_${item.id_mission}${suffix}_company_signed`
        : `contract_${item.id_mission}${suffix}_company`;
      const params = new URLSearchParams({
        missionId: item.id_mission,
        fileName: fileName,
        returnId: item._id,
        status: contract?.status || "0"
      });
      navigate(`/contract/detail?${params.toString()}`);
    }
  };

  const handleMissionCellClick = (e, item) => {
    e.stopPropagation();
    if (hasMoved) return;
    setSelectedId(item._id);
    if (item?._id) {
      navigate(`/mission/${item._id}`);
    }
  };

  return (
    <>
      {champsBody.map((item) =>
        item.contract_company
          .filter((c) => !filterStatus || c.status === filterStatus)
          .map((contract, idx) => {
            const avenantNum = getAvenantNumber(contract);
            const label = contract.id_bestt
              ? avenantNum > 0
                ? `${contract.id_bestt} - ${avenantNum}`
                : String(contract.id_bestt)
              : "—";
            const isSelected = selectedId === contract._id || selectedId === item._id;

            return (
              <tr
                key={contract._id || `${item._id}-${idx}`}
                className={`${isSelected ? 'bg-yellow-50' : 'hover:bg-gray-50'} cursor-pointer transition-colors`}
                onClick={() => handleRowClick(item, contract)}
              >
                <td className="py-5 pl-4 pr-2 min-w-[120px]">{label}</td>
                <td className="py-5 px-2 min-w-[250px]">
                  {item?.userCollection[0]?.firstname ?? null}{" "}
                  {item?.userCollection[0]?.lastname ?? null}
                </td>
                <td className="py-5 px-2 min-w-[300px]">
                  <Tooltip text="Voir la mission" placement="top">
                    <div
                      className={`inline-flex gap-2 items-center px-2 py-1 rounded transition-colors cursor-pointer ${isSelected ? 'hover:bg-yellow-100' : 'hover:bg-gray-100'}`}
                      onClick={(e) => handleMissionCellClick(e, item)}
                    >
                      <span className="underline decoration-dashed">
                        {item?.title ?? null}
                      </span>
                    </div>
                  </Tooltip>
                </td>
                <td className="py-5 px-2">{item?.id_mission ?? null}</td>
                <td className="py-5 px-2 min-w-[100px]">
                  <Chip type="INDICATOR" color={colorContract[contract.status]}>
                    {contentContract[contract.status]}
                  </Chip>
                </td>
                {showPerimeter && (
                  <td className="py-5 px-2">
                    <PerimeterName perimeter={item?.perimeter} />
                  </td>
                )}
                <td className="py-5 px-2">{dateWordShort(contract.createdAt)}</td>
                <TableCell
                  position="last"
                  hasButton
                  buttonOnClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (contract.status === "0" && rUser !== "200") {
                      handleSign(e, contract, item?.id_mission);
                    } else {
                      const aNum = getAvenantNumber(contract);
                      const sfx = aNum > 0 ? `_avenant_${aNum}` : "";
                      const fileName = contract.status === "1"
                        ? `contract_${item.id_mission}${sfx}_company_signed`
                        : `contract_${item.id_mission}${sfx}_company`;
                      const params = new URLSearchParams({
                        missionId: item.id_mission,
                        fileName: fileName,
                        returnId: item._id,
                        status: contract.status || "0"
                      });
                      navigate(`/contract/detail?${params.toString()}`);
                    }
                  }}
                  buttonContent={
                    contract.status === "0" && rUser !== "200" ? (
                      <Signer wh={24} />
                    ) : (
                      <DocumentIcon wh={24} color="#111827" />
                    )
                  }
                  buttonTooltip={
                    contract.status === "0" && rUser !== "200"
                      ? "Signer"
                      : "Consulter"
                  }
                />
              </tr>
            );
          })
      )}
    </>
  );
};

export default Tbodycontract;
